import {
  ActionDropdown,
  BreakPointCondition,
  Icon,
  useBreakpointIf,
  useConfirm,
} from '@genial/components';
import { Color } from '@genial/design-tokens';
import { useNavigation } from '@react-navigation/native';
import React from 'react';
import { ActivityIndicator } from 'react-native';

import { SignedInNavigationScreenNavigationProp } from '../../../../../../../../../routes/types';
import { useFixedFeeActions } from '../../../../../../../../../store/fixedFee/actions';
import { FixedFeeTerm } from '../../../../../../../../../store/fixedFee/types';
import { TermGracePeriodModal } from '../TermGracePeriodModal';

import {
  activeCancelDescription,
  pendingCancelDescription,
  updateTermDescription,
} from './constants';

export interface FixedFeeRowActionsProps {
  feeTerm: FixedFeeTerm;
}

const hasHistory = false;

export const FixedFeeRowActions: React.FC<FixedFeeRowActionsProps> = ({ feeTerm }) => {
  const { confirm } = useConfirm();
  const { postCancelTerm, postResendNotification } = useFixedFeeActions();
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'md');
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();

  const [isLoading, setIsLoading] = React.useState(false);
  const [isGracePeriodModalOpen, setIsGracePeriodModalOpen] = React.useState(false);

  const openGracePeriodModal = () => {
    setIsGracePeriodModalOpen(true);
  };

  const handleUpdateTerm = () => {
    if (!feeTerm.isAbleToReplace) {
      openGracePeriodModal();
      return;
    }

    confirm(
      {
        isMobile: isTablet,
        description: updateTermDescription,
        title: 'Atenção',
        icon: 'Warning',
        iconColor: 'feedback.attention.default',
        confirmText: 'Confirmar',
        confirmColor: 'brand.tertiary.principal',
        cancelText: 'Voltar',
      },
      () =>
        navigation.navigate('FixedFeeNewContractModal', {
          replaceTermContext: {
            termId: feeTerm.id,
            pnpAccountNumber: feeTerm.spnpAccountNumberWithoutDigit || 0,
          },
        })
    );
  };

  const handleHistory = () => {
    navigation.navigate('FixedFeeTermHistoryModal', {
      termId: feeTerm.id,
      term: feeTerm.term,
      customerName: feeTerm.customerName,
      customerCustodyAccount: feeTerm.account,
      status: feeTerm.status,
    });
  };

  const handleCancelTerm = () => {
    if (!feeTerm.isAbleToCancel) {
      openGracePeriodModal();
      return;
    }

    confirm(
      {
        isMobile: isTablet,
        description: feeTerm.activeWithMinimumStayEnded
          ? activeCancelDescription
          : pendingCancelDescription,
        title: 'Atenção',
        icon: 'Warning',
        iconColor: 'feedback.attention.default',
        confirmText: 'Confirmar',
        confirmColor: 'feedback.error.default',
        cancelText: 'Voltar',
      },
      () => {
        setIsLoading(true);
        postCancelTerm(feeTerm.feeBasedContractId, feeTerm.activeWithMinimumStayEnded).finally(() =>
          setIsLoading(false)
        );
      }
    );
  };

  const handleResendNotification = async () => {
    setIsLoading(true);
    postResendNotification(feeTerm.feeBasedContractId).finally(() => setIsLoading(false));
  };

  if (isLoading) {
    return <ActivityIndicator size="small" color={Color['neutral.medium']} />;
  }

  if (
    !feeTerm.isAbleToCancel &&
    !feeTerm.isAbleToReplace &&
    !feeTerm.isAbleToResendNotification &&
    !hasHistory
  ) {
    return <Icon name="MenuExtensionVertical" size="xxxs" color="neutral.lightest" />;
  }

  return (
    <>
      <ActionDropdown
        triggerType="unstyled"
        triggerLabel=""
        triggerIcon="MenuExtensionVertical"
        triggerIconProps={{
          size: 'xxxs',
          color: 'neutral.black',
        }}
        placement="bottom right"
        triggerIconReplacesDropdownIcon
        preventTriggerUnstyledSpace
        extraCustomBoxProps={{ padding: 0 }}>
        {feeTerm.isAbleToReplace && (
          <ActionDropdown.Item
            key="update-term"
            label="Substituir termo"
            icon="Edit"
            iconProps={{ size: 'xxxs', color: 'brand.primary.principal' }}
            onPress={handleUpdateTerm}
          />
        )}
        {feeTerm.isAbleToResendNotification && (
          <ActionDropdown.Item
            key="resend-term"
            label="Reenviar termo"
            icon="Refresh"
            iconProps={{ size: 'xxxs', color: 'brand.primary.principal' }}
            onPress={handleResendNotification}
          />
        )}
        {hasHistory && (
          <ActionDropdown.Item
            key="history"
            label="Ver histórico"
            icon="Info"
            iconProps={{ size: 'xxxs', color: 'brand.primary.principal' }}
            onPress={handleHistory}
          />
        )}
        {feeTerm.isAbleToCancel && (
          <ActionDropdown.Item
            key="cancel-term"
            label="Cancelar termo"
            icon="CloseCircle"
            iconProps={{
              size: 'xxxs',
              color: 'scale.orange.700',
              padding: '2px',
              variant: 'rounded',
            }}
            labelProps={{ color: 'scale.orange.700' }}
            onPress={handleCancelTerm}
          />
        )}
      </ActionDropdown>

      <TermGracePeriodModal
        isOpen={isGracePeriodModalOpen}
        gracePeriodEndDateNormalized={feeTerm.gracePeriodEndDateNormalized}
        onClose={() => setIsGracePeriodModalOpen(false)}
      />
    </>
  );
};
